// Per-saved-view alert config, stored CLIENT-SIDE (per device) under one
// localStorage key. This pairs with the tab-open delivery model: an alert only
// fires where a tab is open, so per-device scope is the natural fit. Toggling a
// view's bell on the scanner page writes here; the global SavedViewAlertsWatcher
// reads here (and subscribes to changes so enabling/disabling takes effect at
// once, including across tabs via the native `storage` event).

export interface ViewAlertConfig {
  /** Fire a browser notification + in-app toast when a fresh hit matches. */
  alert: boolean;
  /** Also play the chime. Only meaningful when `alert` is true. */
  audio: boolean;
}

const KEY = 'siq:scanner-view-alerts:v1';
const EVENT = 'siq-scanner-view-alerts-changed';

type Store = Record<string, ViewAlertConfig>;

function read(): Store {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Store) : {};
  } catch {
    return {};
  }
}

function write(store: Store): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(store));
  } catch {
    /* quota / private mode — alerts just won't persist this session */
  }
  // The native `storage` event only fires in OTHER tabs, so dispatch a custom
  // event for same-tab listeners (the watcher + the toggle UI).
  try {
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {
    /* ignore */
  }
}

export function getAllViewAlerts(): Store {
  return read();
}

export function getViewAlert(id: string): ViewAlertConfig {
  return read()[id] ?? { alert: false, audio: false };
}

export function setViewAlert(id: string, cfg: ViewAlertConfig): void {
  const store = read();
  // Keep the map lean — a view with nothing enabled holds no entry.
  if (!cfg.alert && !cfg.audio) delete store[id];
  else store[id] = cfg;
  write(store);
}

/** Subscribe to config changes — this tab via the CustomEvent, other tabs via
 *  the native `storage` event. Returns an unsubscribe fn. */
export function subscribeViewAlerts(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) cb();
  };
  window.addEventListener(EVENT, cb as EventListener);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, cb as EventListener);
    window.removeEventListener('storage', onStorage);
  };
}
